import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { QueryErrorState } from '@/lib/components';
import type { AiActionsView } from '@/lib/localStorage/aiActionsSortPrefs';

import type { ProposalListItem } from '../../../lib/proposals/core/types';
import { useMyResolvedProposalsInfinite } from '../../../lib/proposals/hooks/useProposals';
import { proposalDetailQuery } from '../../../lib/proposals/query/queries';
import { ActionRowItem } from '../ActionRowItem';
import RecipientAvatar from '../RecipientAvatar';
import { ActionListSkeleton } from '../shared/ActionListSkeleton';

type ResolutionKind = 'approved' | 'rejected' | 'withdrawn';

interface ProposalHistoryListProps {
  orgId: number;
  resolution: ResolutionKind;
  /** Currently viewed action (highlights its row). Unused by the actions
      list page (no selection concept); kept for other surfaces. */
  selectedActionId?: number;
  /** Called with the first row's id when the first page loads. */
  onFirstItemLoaded?: (id: number) => void;
}

const HISTORY_PAGE_SIZE = 50;
const WINDOW_CAPTION = 'Showing the last 7 days';

/** The view tab each history resolution belongs to — passed as a navigation
    state hint so the tab doesn't flash while the action detail loads. */
const VIEW_FOR_RESOLUTION: Record<ResolutionKind, AiActionsView> = {
  approved: 'done',
  rejected: 'dismissed',
  withdrawn: 'archived',
};

const EMPTY_STATE_COPY: Record<
  ResolutionKind,
  { heading: string; body: string }
> = {
  approved: {
    heading: 'No approved actions',
    body: 'Nothing was approved in the last 7 days.',
  },
  rejected: {
    heading: 'No dismissed actions',
    body: 'Nothing was dismissed in the last 7 days.',
  },
  withdrawn: {
    heading: 'No archived actions',
    body: 'Nothing was archived in the last 7 days.',
  },
};

export function ProposalHistoryList({
  orgId,
  resolution,
  selectedActionId,
  onFirstItemLoaded,
}: ProposalHistoryListProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useMyResolvedProposalsInfinite(orgId, resolution, HISTORY_PAGE_SIZE, {
    enabled: orgId > 0,
  });

  const handleRowClick = useCallback(
    (id: number) => {
      navigate(`/${orgId}/actions/${id}`, {
        state: { view: VIEW_FOR_RESOLUTION[resolution] },
      });
    },
    [navigate, orgId, resolution]
  );

  const handleRowMouseDown = useCallback(
    (id: number) => {
      queryClient.prefetchQuery(proposalDetailQuery(orgId, id));
    },
    [queryClient, orgId]
  );

  const getSourceIcon = useCallback(
    (action: ProposalListItem) => (
      <RecipientAvatar action={action} size='xxs' />
    ),
    []
  );

  const results = useMemo(
    () => data?.pages.flatMap(p => p.results) ?? [],
    [data]
  );
  const copy = EMPTY_STATE_COPY[resolution];

  // Report the first loaded row so the parent can auto-select it when
  // nothing is selected (mirrors the Open list's auto-select behavior).
  // Appending pages never changes the first row's id, so this fires once
  // per resolution rather than once per loaded page.
  const firstItemId = results.length > 0 ? results[0].id : null;
  useEffect(() => {
    if (firstItemId != null) {
      onFirstItemLoaded?.(firstItemId);
    }
  }, [firstItemId, onFirstItemLoaded]);

  // Infinite load: fetch the next page when the sentinel below the last row
  // scrolls into view. Re-arming on results.length matters — observe() always
  // reports the initial intersection, so if a short page leaves the sentinel
  // still visible the next page loads without another scroll event.
  // fetchNextPage while a fetch is in flight is a no-op, so no extra guard.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, results.length]);

  return (
    <div className='flex flex-col h-full'>
      {/* Initial load renders the same row skeleton as the Open list, so
          switching tabs never flashes a different loading treatment. */}
      {isLoading && !data && <ActionListSkeleton />}

      {error && (
        <div className='flex flex-1 items-center justify-center'>
          <QueryErrorState error={error} label="We couldn't load history" />
        </div>
      )}

      {!isLoading && !error && data && results.length === 0 && (
        <div className='flex flex-1 items-center justify-center'>
          <EmptyState title={copy.heading} description={copy.body} />
        </div>
      )}

      {!error && results.length > 0 && (
        <div className='flex-1 overflow-y-auto overflow-x-hidden'>
          {results.map(p => (
            <ActionRowItem
              key={p.id}
              isHistory
              action={p}
              isSelected={p.id === selectedActionId}
              isCrmOnly={false}
              isNew={false}
              showShortcuts={false}
              isLastOfLastCategory={false}
              onMouseDown={handleRowMouseDown}
              onClick={handleRowClick}
              getSourceIcon={getSourceIcon}
            />
          ))}
          {/* Sentinel that triggers loading the next page when scrolled into
              view (zero-height; the loader row below is the visible cue). */}
          {hasNextPage && <div ref={sentinelRef} aria-hidden='true' />}
          {isFetchingNextPage && (
            <div
              data-testid='history-loading-more'
              className='flex justify-center border-t border-[var(--color-border-secondary)] py-3'
            >
              <LoadingIndicator type='line-spinner' size='sm' />
            </div>
          )}
          {/* End-of-window marker: the 7-day cutoff only matters once the
              user reaches the end of the list, so it renders after the final
              loaded row rather than as a permanent header. */}
          {!hasNextPage && (
            <p className='px-4 pt-3 pb-4 text-center text-xs text-fg-tertiary'>
              {WINDOW_CAPTION}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
